import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Eye, Trash2, FileDown, Receipt, Wallet, CircleCheck, CircleAlert, MessageCircle, Plus, Pencil, Mail, BadgeDollarSign } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { PageHeader, EmptyState } from "@/components/Common";
import { fmtMoney, fmtPrice, fmtDate } from "@/lib/format";
import { nextReminderDate, relativeDayHint } from "@/lib/reminders";
import { openWaForInvoice } from "@/lib/whatsapp";
import { PdfPreviewDialog } from "@/components/PdfPreviewDialog";
import { NewInvoiceDialog } from "@/components/NewInvoiceDialog";
import { EmailDocumentDialog } from "@/components/EmailDocumentDialog";
import { PaymentDialog } from "@/components/PaymentDialog";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

function InvoiceStatusPill({ value, t }) {
  const map = {
    draft: "bg-slate-100 text-slate-700 ring-slate-200",
    sent: "bg-amber-50 text-amber-800 ring-amber-200",
    paid: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    overdue: "bg-red-50 text-red-700 ring-red-200",
    cancelled: "bg-slate-100 text-slate-500 ring-slate-200",
  };
  return (
    <span data-testid={`invoice-status-${value}`}
      className={`inline-block px-2 py-0.5 rounded-md text-xs font-medium ring-1 ring-inset ${map[value] || map.draft}`}>
      {t("status_" + value)}
    </span>
  );
}

function SummaryCard({ testid, label, tone, currencies, values }) {
  const styles = {
    slate: { card: "border-slate-200 bg-white", iconWrap: "bg-slate-100 text-slate-700", num: "text-slate-900", Icon: Wallet },
    emerald: { card: "border-emerald-200 bg-emerald-50/60", iconWrap: "bg-emerald-100 text-emerald-700", num: "text-emerald-800", Icon: CircleCheck },
    amber: { card: "border-amber-200 bg-amber-50/60", iconWrap: "bg-amber-100 text-amber-800", num: "text-amber-900", Icon: CircleAlert },
  };
  const s = styles[tone] || styles.slate;
  const Icon = s.Icon;
  return (
    <div data-testid={testid} className={`rounded-md border p-4 ${s.card}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="overline">{label}</p>
        <span className={`size-8 grid place-items-center rounded-full ${s.iconWrap}`}>
          <Icon className="size-4" />
        </span>
      </div>
      {currencies.length === 0 ? (
        <p className={`mono text-2xl font-bold ${s.num}`}>0</p>
      ) : (
        <div className="space-y-0.5">
          {values.map((v) => (
            <div key={v.currency} className="flex items-baseline gap-2">
              <p className={`mono text-2xl font-bold leading-tight ${s.num}`}>
                {fmtMoney(v.amount)}
              </p>
              <p className="text-[11px] text-muted-foreground font-medium">{v.currency}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Invoices() {
  const { t, lang } = useI18n();
  const qc = useQueryClient();
  const { data = [], isLoading } = useQuery({ queryKey: ["invoices"], queryFn: () => api.listInvoices() });
  const { data: pendingClaims = [] } = useQuery({
    queryKey: ["payment-claims", "pending"],
    queryFn: () => api.listPaymentClaims({ status: "pending" }),
    staleTime: 60_000,
  });
  const claimedInvoiceIds = new Set(pendingClaims.flatMap((c) => c.invoice_ids || []));
  const { data: reminderSettings } = useQuery({
    queryKey: ["reminder-settings"],
    queryFn: () => api.getReminderSettings(),
    staleTime: 5 * 60_000,
  });
  const invoiceInterval = reminderSettings?.invoice_reminder_days || 15;
  const [confirmId, setConfirmId] = useState(null);
  const [filter, setFilter] = useState("all"); // all | unpaid | paid
  const [preview, setPreview] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [emailTarget, setEmailTarget] = useState(null);
  const [payTarget, setPayTarget] = useState(null);

  const counts = data.reduce(
    (acc, inv) => {
      const isPaid = inv.status === "paid" || Number(inv.balance_due || 0) <= 0.005;
      if (isPaid) acc.paid += 1; else acc.unpaid += 1;
      return acc;
    },
    { unpaid: 0, paid: 0 }
  );

  // Money summary — grouped by currency because invoices can be in USD or IDR.
  const moneySummary = data.reduce((acc, inv) => {
    const cur = inv.currency || "USD";
    const bucket = acc[cur] || { paid: 0, unpaid: 0, total: 0 };
    const total = Number(inv.total || 0);
    const paid = Number(inv.paid || 0);
    const bal = Number(inv.balance_due ?? total - paid);
    bucket.total += total;
    bucket.paid += paid;
    bucket.unpaid += Math.max(0, bal);
    acc[cur] = bucket;
    return acc;
  }, {});
  const currencies = Object.keys(moneySummary).sort();
  const todayStr = new Date().toISOString().slice(0, 10);
  const isOverdue = (inv) => {
    if (inv.status === "paid" || inv.status === "cancelled") return false;
    const bal = Number(
      inv.balance_due ?? (Number(inv.total || 0) - Number(inv.paid || 0))
    );
    if (bal <= 0.005) return false;
    if (!inv.due_date) return false;
    return String(inv.due_date).slice(0, 10) < todayStr;
  };
  const overdueCount = data.filter(isOverdue).length;
  const filtered = data
    .filter((inv) => {
      if (filter === "all") return true;
      if (filter === "overdue") return isOverdue(inv);
      const isPaid = inv.status === "paid" || Number(inv.balance_due || 0) <= 0.005;
      return filter === "paid" ? isPaid : !isPaid;
    })
    .sort((a, b) => {
      const ao = isOverdue(a);
      const bo = isOverdue(b);
      if (ao !== bo) return ao ? -1 : 1;
      if (ao && bo) {
        // Oldest due date first (most overdue at the top)
        return String(a.due_date || "").localeCompare(String(b.due_date || ""));
      }
      return 0;
    });

  const deleteMut = useMutation({
    mutationFn: (id) => api.deleteInvoice(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["invoices"] });
      setConfirmId(null);
      toast.success("Deleted");
    },
  });

  const tabs = [
    { id: "all", label: t("all") || "All", count: data.length, tone: "slate" },
    { id: "overdue", label: t("status_overdue") || "Overdue", count: overdueCount, tone: "red" },
    { id: "unpaid", label: t("unpaid") || "Unpaid", count: counts.unpaid, tone: "amber" },
    { id: "paid", label: t("paid") || "Paid", count: counts.paid, tone: "emerald" },
  ];

  return (
    <div data-testid="invoices-page">
      <div className="flex items-start justify-between gap-3 mb-3">
        <PageHeader overline={t("invoices")} title={t("invoices")} />
        <Button
          data-testid="new-invoice-btn"
          className="bg-primary hover:bg-primary/90"
          onClick={() => setNewOpen(true)}
        >
          <Plus className="size-4 mr-1.5" /> New invoice
        </Button>
      </div>

      {/* Money summary — grouped by currency */}
      {data.length > 0 && (
        <div
          data-testid="invoices-money-summary"
          className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5"
        >
          <SummaryCard
            testid="invoices-summary-total"
            label="Total invoiced"
            tone="slate"
            currencies={currencies}
            values={currencies.map((c) => ({ currency: c, amount: moneySummary[c].total }))}
          />
          <SummaryCard
            testid="invoices-summary-paid"
            label="Paid"
            tone="emerald"
            currencies={currencies}
            values={currencies.map((c) => ({ currency: c, amount: moneySummary[c].paid }))}
          />
          <SummaryCard
            testid="invoices-summary-unpaid"
            label="Unpaid / Balance due"
            tone="amber"
            currencies={currencies}
            values={currencies.map((c) => ({ currency: c, amount: moneySummary[c].unpaid }))}
          />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="invoices-tabs">
        {tabs.map((tab) => {
          const active = filter === tab.id;
          const toneMap = {
            slate: active ? "bg-slate-800 text-white ring-slate-800" : "bg-white text-slate-700 ring-slate-200 hover:bg-slate-50",
            amber: active ? "bg-amber-500 text-white ring-amber-500" : "bg-white text-amber-700 ring-amber-200 hover:bg-amber-50",
            emerald: active ? "bg-emerald-600 text-white ring-emerald-600" : "bg-white text-emerald-700 ring-emerald-200 hover:bg-emerald-50",
            red: active ? "bg-red-600 text-white ring-red-600" : "bg-white text-red-700 ring-red-200 hover:bg-red-50",
          };
          return (
            <button
              key={tab.id}
              data-testid={`invoices-tab-${tab.id}`}
              onClick={() => setFilter(tab.id)}
              className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-medium ring-1 ring-inset transition ${toneMap[tab.tone]}`}
            >
              {tab.label}
              <span className={`text-[10px] mono px-1.5 py-0.5 rounded-full ${active ? "bg-white/25" : "bg-slate-100 text-slate-600"}`}>
                {tab.count}
              </span>
            </button>
          );
        })}
      </div>

      {isLoading ? <p className="text-muted-foreground">{t("loading")}</p>
        : filtered.length === 0 ? (
          <EmptyState title={t("no_data")} hint={t("create_invoice")} />
        ) : (
          <div className="border border-border rounded-md bg-white overflow-x-auto" data-testid="invoices-table-wrap">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-b border-border">
                  <TableHead className="overline">{t("invoice_number")}</TableHead>
                  <TableHead className="overline">{t("client")}</TableHead>
                  <TableHead className="overline">{t("issue_date")}</TableHead>
                  <TableHead className="overline">{t("due_date")}</TableHead>
                  <TableHead className="overline">{t("source")}</TableHead>
                  <TableHead className="overline">{t("status")}</TableHead>
                  <TableHead className="overline text-right">{t("total")}</TableHead>
                  <TableHead className="overline text-right">{t("actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((inv) => {
                  const overdue = isOverdue(inv);
                  return (
                  <TableRow
                    key={inv.id}
                    data-testid={`invoice-row-${inv.id}`}
                    data-overdue={overdue ? "true" : undefined}
                    className={overdue ? "bg-red-50/40" : undefined}
                  >
                    <TableCell className="font-medium mono">
                      <Link to={`/invoices/${inv.id}`} className="text-primary hover:underline flex items-center gap-1.5">
                        <Receipt className="size-3.5" /> {inv.invoice_number}
                      </Link>
                    </TableCell>
                    <TableCell>{inv.client_snapshot?.name || "—"}</TableCell>
                    <TableCell className="text-xs mono text-muted-foreground">{fmtDate(inv.issue_date)}</TableCell>
                    <TableCell className="text-xs mono text-muted-foreground">
                      <div className="flex items-center gap-1.5">
                        <span>{fmtDate(inv.due_date) || "—"}</span>
                        {overdue && (
                          <span
                            data-testid={`invoice-overdue-tag-${inv.id}`}
                            className="inline-block px-1.5 py-0.5 rounded-md text-[10px] font-semibold ring-1 ring-inset bg-red-50 text-red-700 ring-red-200"
                          >
                            OVERDUE
                          </span>
                        )}
                        {(() => {
                          if (inv.status === "paid" || overdue) return null;
                          const next = nextReminderDate({
                            anchor: inv.issue_date,
                            lastRemindedAt: inv.reminder_sent_at,
                            intervalDays: invoiceInterval,
                          });
                          if (!next) return null;
                          return (
                            <span
                              data-testid={`invoice-next-reminder-${inv.id}`}
                              title={`Auto-reminder ${relativeDayHint(next)} (${next})`}
                              className="inline-block px-1.5 py-0.5 rounded-md text-[10px] font-medium ring-1 ring-inset bg-sky-50 text-sky-700 ring-sky-200"
                            >
                              nudge {relativeDayHint(next)}
                            </span>
                          );
                        })()}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground mono">{inv.source_number || inv.source}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <InvoiceStatusPill value={inv.status} t={t} />
                        {claimedInvoiceIds.has(inv.id) && (
                          <Link to={`/invoices/${inv.id}`}
                                data-testid={`invoice-claim-badge-${inv.id}`}
                                title="A client reported a payment — review it"
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold ring-1 ring-inset bg-amber-50 text-amber-800 ring-amber-300 hover:bg-amber-100">
                            <BadgeDollarSign className="size-3" /> Client reported payment
                          </Link>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right mono font-semibold">
                      {fmtPrice(inv.total, inv.currency)}
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      <Link to={`/invoices/${inv.id}`}>
                        <Button data-testid={`view-invoice-${inv.id}`} variant="ghost" size="sm">
                          <Eye className="size-3.5" />
                        </Button>
                      </Link>
                      <Link to={`/invoices/${inv.id}?edit=1`}>
                        <Button
                          data-testid={`edit-invoice-${inv.id}`}
                          variant="ghost"
                          size="sm"
                          className="text-primary"
                          title="Edit invoice"
                        >
                          <Pencil className="size-3.5" />
                        </Button>
                      </Link>
                      <Button data-testid={`pdf-invoice-${inv.id}`} variant="ghost" size="sm" className="text-primary" onClick={() => setPreview({ id: inv.id, number: inv.invoice_number })}>
                        <FileDown className="size-3.5" />
                      </Button>
                      <Button
                        data-testid={`email-invoice-${inv.id}`}
                        variant="ghost"
                        size="sm"
                        className="text-primary"
                        title="Email invoice to client (with PDF attachment)"
                        onClick={() => setEmailTarget(inv)}
                      >
                        <Mail className="size-3.5" />
                      </Button>
                      {!(inv.status === "paid" || Number(inv.balance_due || 0) <= 0.005) && (
                        <Button
                          data-testid={`mark-paid-${inv.id}`}
                          variant="ghost"
                          size="sm"
                          className="text-emerald-700 hover:bg-emerald-50"
                          title="Record payment (Full / Deposit / Half — Cash / Bank / Cheque). Auto-emails a receipt to the client."
                          onClick={() => setPayTarget(inv)}
                        >
                          <CircleCheck className="size-3.5" />
                        </Button>
                      )}
                      <Button
                        data-testid={`wa-invoice-${inv.id}`}
                        variant="ghost"
                        size="sm"
                        className="text-emerald-600 hover:text-emerald-700"
                        onClick={() => openWaForInvoice(inv, lang)}
                        title="WhatsApp follow-up"
                      >
                        <MessageCircle className="size-3.5" />
                      </Button>
                      <Button data-testid={`delete-invoice-${inv.id}`} variant="ghost" size="sm"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => setConfirmId(inv.id)}>
                        <Trash2 className="size-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

      <AlertDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("confirm_delete")}</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-delete-invoice-cancel">{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-invoice-ok"
              onClick={() => deleteMut.mutate(confirmId)}
              className="bg-red-600 hover:bg-red-700">
              {t("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <PdfPreviewDialog
        open={!!preview}
        onOpenChange={(v) => !v && setPreview(null)}
        title={preview ? `Invoice ${preview.number || ""}` : ""}
        filename={preview ? `invoice-${preview.number || preview.id}.pdf` : "invoice.pdf"}
        fetchBlob={preview ? () => api.fetchInvoicePdfBlob(preview.id) : null}
        fetchPngBlob={preview ? () => api.fetchInvoicePngBlob(preview.id) : null}
        pngFilename={preview ? `invoice-${preview.number || preview.id}.png` : null}
      />

      <NewInvoiceDialog
        open={newOpen}
        onOpenChange={setNewOpen}
      />

      <EmailDocumentDialog
        open={!!emailTarget}
        onOpenChange={(v) => !v && setEmailTarget(null)}
        kind="invoice"
        defaultTo={emailTarget?.client_snapshot?.email || ""}
        defaultClientName={emailTarget?.client_snapshot?.name || ""}
        sendFn={(payload) => api.emailInvoice(emailTarget.id, payload)}
        testIdPrefix="email-invoice"
      />

      <PaymentDialog
        open={!!payTarget}
        onOpenChange={(v) => !v && setPayTarget(null)}
        invoice={payTarget}
        onSaved={() => {
          qc.invalidateQueries({ queryKey: ["invoices"] });
          setPayTarget(null);
        }}
      />

    </div>
  );
}
